<template>
    <!-- A toast for the outcome of an action ("Success: This report will be
         emailed to you"). Place it at the top of the page's content column: it
         sticks to the top of the scroll area, centred over that column (so the
         app sidebar never pulls it off-centre), and floats over the content
         without taking up space. Closes itself after `duration` ms; hovering
         holds it open. -->
    <div class="pointer-events-none sticky top-4 z-50 flex h-0 items-start justify-center">
        <transition enter-active-class="transition duration-200 ease-out"
                    enter-from-class="-translate-y-2 opacity-0"
                    leave-active-class="transition duration-150 ease-in"
                    leave-to-class="-translate-y-2 opacity-0"
        >
            <div    v-if="show"
                    role="status"
                    aria-live="polite"
                    class="pointer-events-auto flex w-full max-w-2xl items-center gap-4 rounded-lg px-6 py-4 text-lg text-white shadow-2xl ring-1 ring-black/10"
                    :class="type === 'error' ? 'bg-red-600' : 'bg-green-500'"
                    @mouseenter="stopTimer"
                    @mouseleave="startTimer"
            >
                <tabler-icon    :name="type === 'error' ? 'alertCircle' : 'circleCheck'"
                                size-class="h-6 w-6"
                                :stroke-width="2"
                ></tabler-icon>

                <p class="m-0 flex-1 text-left">
                    <span   v-if="title"
                            class="font-semibold">{{ title }}:</span>{{ ' ' }}
                    <slot></slot>
                </p>

                <button type="button"
                        class="shrink-0 rounded p-1 text-white/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label="Dismiss"
                        @click="close"
                >
                    <tabler-icon    name="x"
                                    size-class="h-5 w-5"
                                    :stroke-width="2"
                    ></tabler-icon>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
import TablerIcon from "./TablerIcon.vue"

export default {
    name: "AlertToast",
    components: {TablerIcon},
    emits: ['update:show'],
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        // 'success' (green) or 'error' (red).
        type: {
            type: String,
            default: 'success',
            validator: (value) => ['success', 'error'].includes(value),
        },
        // Bold lead-in before the message, e.g. "Success".
        title: {
            type: String,
            default: null,
        },
        // How long the toast stays up, in ms. 0 keeps it until closed.
        duration: {
            type: Number,
            default: 6000,
        },
    },
    data() {
        return {
            timer: null,
        }
    },
    watch: {
        show: {
            handler(value) {
                value ? this.startTimer() : this.stopTimer()
            },
            immediate: true,
        },
    },
    beforeUnmount() {
        this.stopTimer()
    },
    methods: {
        startTimer() {
            this.stopTimer()

            if (this.show && this.duration > 0) {
                this.timer = setTimeout(this.close, this.duration)
            }
        },
        stopTimer() {
            clearTimeout(this.timer)
            this.timer = null
        },
        close() {
            this.stopTimer()
            this.$emit('update:show', false)
        },
    },
}
</script>
